import dayjs from 'dayjs';
import pluralize from 'pluralize';

import type { PublicSignerLinkState } from '../publicSignerError';
import {
  type PublicSignerSessionResponse,
  type PublicSignerSubmitResponse,
  PublicSignerSubmitSuccessResponseCompletionStateEnum
} from '../publicSignerTypes';

export function getLinkStatePresentation(
  linkState: PublicSignerLinkState | null | undefined
) {
  switch (linkState) {
    case 'expired':
      return {
        description:
          'This signing link has expired. Request a fresh link from the sender.',
        severity: 'error' as const,
        title: 'Link expired'
      };
    case 'superseded':
      return {
        description:
          'A newer signing link has already been issued for this signer.',
        severity: 'error' as const,
        title: 'Link replaced'
      };
    case 'completed':
      return {
        description: 'This signer has already completed the document.',
        severity: 'success' as const,
        title: 'Signing complete'
      };
    case 'cancelled':
      return {
        description:
          'This signing request has been cancelled and is no longer available.',
        severity: 'error' as const,
        title: 'Signing cancelled'
      };
    case 'rate_limited':
      return {
        description:
          'Too many requests right now. Please wait a moment and try again.',
        severity: 'error' as const,
        title: 'Temporarily unavailable'
      };
    case 'server_error':
      return {
        description:
          'Something went wrong on our end. Please try again in a few moments.',
        severity: 'error' as const,
        title: 'Something went wrong'
      };
    case 'invalid':
    default:
      return {
        description:
          'This signing link could not be validated. Check the URL or request a new link.',
        severity: 'error' as const,
        title: 'Invalid link'
      };
  }
}

export function getCompletionPresentation(
  submitResult?: PublicSignerSubmitResponse | null
) {
  if (
    submitResult?.completion_state ===
    PublicSignerSubmitSuccessResponseCompletionStateEnum.RequestCompleted
  ) {
    return {
      description:
        'Your signature has been submitted and this request is now fully complete.',
      title: 'Signing complete'
    };
  }

  return {
    description:
      'Your signature has been submitted successfully. Additional signers may still need to complete this document.',
    title: 'Signing complete'
  };
}

export function formatExpiryCountdown(value: string | Date) {
  const expiry = dayjs(value);

  if (!expiry.isValid()) {
    return 'Secure link';
  }

  const now = dayjs();
  const minutesRemaining = expiry.diff(now, 'minute', true);

  if (minutesRemaining <= 0) {
    return 'Expired';
  }

  const daysRemaining = expiry.diff(now, 'day', true);
  if (daysRemaining >= 1) {
    return pluralize('Day', Math.ceil(daysRemaining), true);
  }

  const hoursRemaining = expiry.diff(now, 'hour', true);
  if (hoursRemaining >= 1) {
    return pluralize('Hour', Math.ceil(hoursRemaining), true);
  }

  return pluralize('Min', Math.max(1, Math.ceil(minutesRemaining)), true);
}

export function getFieldProgressLabel(
  completedFieldCount: number,
  totalFieldCount: number
) {
  if (totalFieldCount === 0) {
    return 'No fields';
  }

  if (completedFieldCount >= totalFieldCount) {
    return 'All complete';
  }

  return `${totalFieldCount - completedFieldCount} left`;
}

export function getDisclosureRequestTitle(
  session: PublicSignerSessionResponse
) {
  return session.request_title?.trim() || 'this document';
}

export function getDisclosureFacilityName(
  session: PublicSignerSessionResponse
) {
  return session.facility_name?.trim() || null;
}

export function getDisclosureSignerLabel(session: PublicSignerSessionResponse) {
  const signerName = session.signer_name?.trim();
  const signerRole = session.signer_role?.trim();

  if (signerName && signerRole) {
    return `${signerName} - ${signerRole}`;
  }

  return signerName || signerRole || null;
}
